import { useRef, memo } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'

import { PB50T, PB40T, PB70T, PB90T, PG, PG90T, WHITE } from '../../colors'
import { Flex } from '../../layout/flex'
import { base } from '../../mixins'

const getBorder = ({ isChecked, isDisabled, checkedStyle, status = 'default' }) => {
  if (isDisabled) {
    return `2px solid ${PB90T}`
  }

  if (isChecked) {
    if (checkedStyle === 'default') {
      return `2px solid ${PG}`
    }

    return `2px solid ${PB70T}`
  }

  if (status === 'hover') {
    return `2px solid ${PG}`
  }

  return `2px solid ${PB70T}`
}
const getBackgroundColor = ({
  isChecked,
  isDisabled,
  checkedStyle,
  status = 'default'
}) => {
  if (isDisabled) {
    return PB90T
  }

  if (isChecked) {
    if (checkedStyle === 'default') {
      return PG
    }

    return PB70T
  }

  if (status === 'hover') {
    return PG90T
  }

  return 'white'
}
const getLabelColor = ({ isDisabled, labelColor }) => {
  if (isDisabled) {
    return PB90T
  }

  return labelColor
}
const Label = styled.label`
  ${base}
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  position: relative;
  color: ${getLabelColor};
  font-size: 14px;
  font-weight: 500;
  margin-left: 4px;
  display: inline-flex;
  align-items: center;
`
const Icon = styled.svg`
  fill: none;
  position: absolute;
  stroke: ${({ isDisabled }) => (isDisabled ? PB50T : WHITE)};
  stroke-width: 4px;
`
const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  outline: 0;
  position: absolute;
  display: none;
`
const CheckFocusedBorder = styled.span`
  position: absolute;
  top: -6px;
  left: -6px;
  height: 28px;
  width: 28px;
  border: 2px solid ${PG};
  border-radius: 6px;
  outline: none;
  background-color: rgba(255, 255, 255, 0);
  visibility: hidden;
`
const StyledCheckbox = styled.div`
  display: inline-block;
  position: relative;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: ${({ isChecked, isDisabled, checkedStyle }) =>
    getBorder({ isChecked, isDisabled, checkedStyle })};
  background-color: ${({ isChecked, isDisabled, checkedStyle }) =>
    getBackgroundColor({ isChecked, isDisabled, checkedStyle })};

  ${HiddenCheckbox}:hover + && {
    border: ${props => getBorder({ ...props, status: 'hover' })};
    background-color: ${props => getBackgroundColor({ ...props, status: 'hover' })};
    box-shadow: ${({ isDisabled }) =>
      isDisabled ? 'none' : '0 2px 12px 0 rgba(32, 30, 29, 0.20)'};
  }

  ${HiddenCheckbox}:focus + && {
    > ${CheckFocusedBorder} {
      visibility: ${({ isDisabled, isSelectOption }) =>
        isDisabled || isSelectOption ? 'hidden' : 'visible'};
    }
  }

  ${Icon} {
    visibility: ${({ isChecked }) => (isChecked ? 'visible' : 'hidden')};
  }
`
/**
 * The Checkbox component can be used as a standard form checkbox field.
 */
const CheckboxComponent = ({
  onChangeArg,
  label = '',
  labelColor = PB40T,
  isChecked = false,
  isDisabled = false,
  isSelectOption = false,
  onChange = () => {},
  checkedStyle = 'default',
  ...props
}) => {
  let keyDown = false
  const inputRef = useRef(null)
  const onClick = () => {
    if (!keyDown) {
      inputRef.current.blur()
    }

    keyDown = false

    return keyDown
  }
  const onKeyDown = () => {
    keyDown = true

    return keyDown
  }
  const inputProps = {
    checked: isChecked,
    disabled: isDisabled,
    onChange: onChangeArg !== undefined ? () => onChange(onChangeArg) : onChange,
    onKeyDown,
    onClick
  }
  const styledCheckboxProps = {
    isChecked,
    isDisabled,
    checkedStyle,
    isSelectOption
  }
  const renderIcon = () => {
    if (checkedStyle === 'default') {
      return <polyline points="20 6 9 17 4 12" />
    }

    return <line x1="2" y1="12" x2="22" y2="12" stroke="white" />
  }

  return (
    <Label isDisabled={isDisabled} labelColor={labelColor} className={props.className}>
      <Flex alignItems="center">
        <HiddenCheckbox ref={inputRef} {...inputProps} />
        <StyledCheckbox {...styledCheckboxProps}>
          <Icon viewBox="0 0 24 24" isDisabled={isDisabled}>
            {renderIcon()}
          </Icon>
          <CheckFocusedBorder />
        </StyledCheckbox>
      </Flex>
      <span style={{ marginLeft: '12px' }}>{label}</span>
    </Label>
  )
}

CheckboxComponent.propTypes = {
  /** Set the label of the checkbox */
  label: PropTypes.string,
  /** The color of the label text. */
  labelColor: PropTypes.string,
  /** Determine the status of the checkbox */
  isChecked: PropTypes.bool,
  /** Callback when the fields value changes. Is passed the synthetic event object. */
  onChange: PropTypes.func,
  /** An argument to pass to onChange when its called. Useful for optimizing event handlers when generating lots of elements in a loop. */
  onChangeArg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Determine if the component is disabled */
  isDisabled: PropTypes.bool,
  /**
   * Determine if the checkbox is part of the select option
   * @ignore
   */
  isSelectOption: PropTypes.bool,
  /** The style of the checkbox when it's checked */
  checkedStyle: PropTypes.oneOf(['default', 'line'])
}

const Checkbox = memo(CheckboxComponent)

export { Checkbox }
